<!-- `--wide` (1200px, styles.scss:528) rather than the 1024px default: this is a
     dense eight-column Ops log, and the extra 176px is what lets every column
     hold its own content instead of being squeezed into an ellipsis. -->
<div class="dash-shell dash-shell--wide">
  <!-- ═══════════════════════════════════════════════════════════════════════
       PAGE HEADER
       ═══════════════════════════════════════════════════════════════════════ -->
  <header class="page-head">
    <nav class="page-head__crumbs" aria-label="Breadcrumb">
      <a class="page-head__crumb" routerLink="/dashboard">Home</a>
      <span class="page-head__sep" aria-hidden="true">/</span>
      <a class="page-head__crumb" routerLink="/admin/dashboard">Admin</a>
      <span class="page-head__sep" aria-hidden="true">/</span>
      <span class="page-head__crumb page-head__crumb--current" aria-current="page"
        >AI evaluations</span
      >
    </nav>
    <div class="page-head__bar">
      <div class="page-head__titles">
        <h1 class="page-head__title">AI evaluations</h1>
        <p class="page-head__subtitle">
          Every Claude evaluation call across Kickstart Cash and Debtors Finance — its advisory
          recommendation, token usage and cost. Internal only; never shown to clients.
        </p>
      </div>
    </div>
  </header>

  <!-- ─── Spend summary ─────────────────────────────────────────────────── -->
  @if (summary(); as s) {
    <section class="dash-section" aria-label="AI spend summary">
      <div class="dash-kpi-grid dash-kpi-grid--2">
        <div class="dash-kpi dash-kpi--compact">
          <div class="dash-kpi__top">
            <span class="dash-kpi__icon"><app-icon name="sparkles" [size]="18" /></span>
          </div>
          <div>
            <p class="dash-kpi__value">{{ s.total_evaluations | dash }}</p>
            <p class="dash-kpi__label">Evaluations</p>
          </div>
        </div>

        <div class="dash-kpi dash-kpi--compact">
          <div class="dash-kpi__top">
            <span class="dash-kpi__icon"><app-icon name="tag" [size]="18" /></span>
          </div>
          <div>
            <p class="dash-kpi__value">{{ s.cost_usd_total | usd }}</p>
            <p class="dash-kpi__label">Total spend</p>
          </div>
        </div>
      </div>
    </section>
  }

  <!-- ─── How each model has done ───────────────────────────────────────── -->
  @if (modelRows().length) {
    <section class="dash-section aiev-models" aria-label="How each model has done">
      <h2 class="aiev-models__title">How each model has done</h2>
      <div class="aiev-models__scroll">
        <table class="aiev-models__table" data-testid="ai-model-summary">
          <thead>
            <tr>
              <th scope="col">Call</th>
              <th scope="col">Model</th>
              <th scope="col" class="aiev-models__num">Complete</th>
              <th scope="col" class="aiev-models__num">Avg cost (ZAR)</th>
              <th scope="col" class="aiev-models__num">Avg time</th>
              <th scope="col" class="aiev-models__num">Avg input</th>
              <th scope="col" class="aiev-models__num">Avg cache read</th>
              <th scope="col" class="aiev-models__num">Avg cache written</th>
              <th scope="col" class="aiev-models__num">Avg output</th>
            </tr>
          </thead>
          <tbody>
            @for (row of modelRows(); track row.purpose + row.model) {
              <tr>
                <td>{{ row.purpose_display }}</td>
                <td>{{ row.model }}</td>
                <td class="aiev-models__num">{{ row.complete }} of {{ row.calls }}</td>
                <td class="aiev-models__num">{{ row.avg_cost_zar | number: '1.2-4' }}</td>
                <td class="aiev-models__num">
                  {{ row.avg_latency_ms / 1000 | number: '1.0-0' }} sec
                </td>
                <td class="aiev-models__num">{{ row.avg_input_tokens | number }}</td>
                <td class="aiev-models__num">{{ row.avg_cache_read_tokens | number }}</td>
                <td class="aiev-models__num">{{ row.avg_cache_write_tokens | number }}</td>
                <td class="aiev-models__num">{{ row.avg_output_tokens | number }}</td>
              </tr>
            }
          </tbody>
        </table>
      </div>
    </section>
  }

  <!-- ─── Filters ───────────────────────────────────────────────────────── -->
  <section class="aiev-filters" aria-label="Filters">
    <mat-form-field appearance="outline" class="aiev-filters__field">
      <mat-label>Product</mat-label>
      <mat-select [value]="purposeFilter()" (valueChange)="onPurposeChange($event)">
        <mat-option value="">All products</mat-option>
        @for (p of purposeOptions(); track p.value) {
          <mat-option [value]="p.value">{{ p.label }}</mat-option>
        }
      </mat-select>
    </mat-form-field>

    <mat-form-field appearance="outline" class="aiev-filters__field">
      <mat-label>Recommendation</mat-label>
      <mat-select [value]="recommendationFilter()" (valueChange)="onRecommendationChange($event)">
        <mat-option value="">All recommendations</mat-option>
        @for (r of recommendationOptions(); track r.value) {
          <mat-option [value]="r.value">{{ r.label }}</mat-option>
        }
      </mat-select>
    </mat-form-field>
  </section>

  <!-- ═══════════════════════════════════════════════════════════════════════
       TABLE
       ═══════════════════════════════════════════════════════════════════════ -->
  <section class="dash-section" aria-label="AI evaluation log">
    <div class="sg-panel dash-panel">
      <div class="dash-table-wrap">
        @if (loading()) {
          <table class="sg-table sg-table--skeleton">
            <thead>
              <tr>
                <th>Product</th>
                <th>Summary</th>
                <th>Recommendation</th>
                <th>Prompt</th>
                <th class="sg-cell--num">Tokens</th>
                <th class="sg-cell--num">Cost</th>
                <th>Requested by</th>
                <th>When</th>
              </tr>
            </thead>
            <tbody>
              @for (r of [1, 2, 3, 4, 5, 6, 7, 8]; track r) {
                <tr>
                  <td><span class="sg-skeleton sg-skeleton--chip"></span></td>
                  <td><span class="sg-skeleton sg-skeleton--text sg-skeleton--w80"></span></td>
                  <td><span class="sg-skeleton sg-skeleton--chip"></span></td>
                  <td><span class="sg-skeleton sg-skeleton--text sg-skeleton--w50"></span></td>
                  <td class="sg-cell--num">
                    <span
                      class="sg-skeleton sg-skeleton--text"
                      style="width: 60%; margin-left: auto"
                    ></span>
                  </td>
                  <td class="sg-cell--num">
                    <span
                      class="sg-skeleton sg-skeleton--text"
                      style="width: 60%; margin-left: auto"
                    ></span>
                  </td>
                  <td><span class="sg-skeleton sg-skeleton--text sg-skeleton--w70"></span></td>
                  <td><span class="sg-skeleton sg-skeleton--text sg-skeleton--w60"></span></td>
                </tr>
              }
            </tbody>
          </table>
        } @else if (error()) {
          <div class="sg-empty sg-empty--bordered">
            <app-icon name="x-circle" [size]="40" />
            <p class="sg-empty__title">Couldn't load AI evaluations</p>
            <p class="sg-empty__desc">Something went wrong on our side.</p>
            <button mat-stroked-button (click)="retry()">
              <app-icon name="refresh" [size]="18" /> Try again
            </button>
          </div>
        } @else if (!groups().length) {
          <div class="dash-chart-empty dash-chart-empty--pad">
            <app-icon name="sparkles" [size]="32" />
            <p>No AI evaluations match this filter yet</p>
          </div>
        } @else {
          <table class="sg-table aiev-table">
            <thead>
              <tr>
                <th>Product</th>
                <th>Summary</th>
                <th>Recommendation</th>
                <th>Prompt</th>
                <!-- Header kept shorter than its own data: 'Tokens (in / out)'
                     set this column's width, not the numbers under it. -->
                <th class="sg-cell--num" title="Total input tokens / output tokens">Tokens</th>
                <th class="sg-cell--num">Cost</th>
                <th>Requested by</th>
                <th>When</th>
              </tr>
            </thead>
            <!-- One tbody per subject: a group header row, then its calls
                 newest-first. The subject's identity lives in the header, which
                 is what keeps the call rows narrow enough to fit (FLOW-221). -->
            @for (g of groups(); track g.target_kind + g.target_id) {
              <tbody class="aiev-group">
                <!-- One full-width cell, laid out with flex. A colspan short of
                     the full width would set a minimum on the columns it spans
                     and squeeze the real ones (it cost Summary 180px). -->
                <tr class="aiev-group__head">
                  <th scope="colgroup" [attr.colspan]="8">
                    <!-- The flex box is INSIDE the cell on purpose: `display:
                         flex` on a <th> takes it out of table layout, colspan
                         stops applying, and the header then sizes the first
                         column instead of spanning all eight. -->
                    <span class="aiev-group__id">
                      <span class="aiev-group__kind">{{ g.target_kind_display }}</span>
                      <span class="aiev-group__target">{{ g.target_id }}</span>
                      <span class="aiev-group__count">
                        {{ g.call_count | number }}
                        {{ g.call_count === 1 ? 'call' : 'calls' }}
                      </span>
                      <span class="aiev-group__meta">
                        <span class="aiev-group__cost">{{ g.cost_usd | usd }}</span>
                        <span class="aiev-group__when">
                          {{ g.latest_created_at | date: 'd MMM y, HH:mm' }}
                        </span>
                      </span>
                    </span>
                  </th>
                </tr>
                @for (e of g.calls; track e.short_id) {
                  <tr
                    class="sg-table__row aiev-row"
                    [routerLink]="['/admin/ai-evaluations', e.short_id]"
                    tabindex="0"
                    role="link"
                    (keyup.enter)="openDetail(e.short_id)"
                  >
                    <td>
                      <span class="aiev-product">{{ e.purpose_display }}</span>
                    </td>
                    <td class="aiev-subject">
                      @if (e.summary) {
                        <span class="aiev-subject__summary">{{ e.summary }}</span>
                      } @else {
                        <span class="dash-muted">—</span>
                      }
                    </td>
                    <td>
                      @if (e.recommendation) {
                        <span
                          class="aiev-rec"
                          [attr.data-variant]="recVariant(e.recommendation)"
                          [attr.title]="e.recommendation_display"
                          [attr.aria-label]="e.recommendation_display"
                        >
                          {{ recShort(e.recommendation, e.recommendation_display) }}
                        </span>
                      } @else {
                        <span class="dash-muted">—</span>
                      }
                    </td>
                    <td class="aiev-muted">{{ e.prompt_version | dash }}</td>
                    <td class="sg-cell--num aiev-muted">
                      {{ e.total_input_tokens | number }} / {{ e.output_tokens | number }}
                    </td>
                    <td class="sg-cell--num">{{ e.cost_usd | usd }}</td>
                    <td class="aiev-muted">{{ e.requested_by_name | dash }}</td>
                    <td class="aiev-muted aiev-when">
                      {{ e.created_at | date: 'd MMM y, HH:mm' }}
                    </td>
                  </tr>
                }
              </tbody>
            }
          </table>
        }
      </div>

      <!-- ─── Pagination footer ─────────────────────────────────────────── -->
      @if (!loading() && !error() && showPager()) {
        <div class="aiev-pager">
          <!-- The unit is subjects, not calls: the backend paginates by group. -->
          <span class="aiev-pager__range">
            {{ rangeStart() }}–{{ rangeEnd() }} of {{ total() | number }} subjects
            <span class="aiev-pager__calls">({{ totalCalls() | number }} calls)</span>
          </span>
          <div class="aiev-pager__nav">
            <button mat-stroked-button type="button" (click)="prevPage()" [disabled]="page() <= 1">
              <app-icon name="chevron-left" [size]="16" /> Previous
            </button>
            <button mat-stroked-button type="button" (click)="nextPage()" [disabled]="!hasNext()">
              Next <app-icon name="chevron-right" [size]="16" />
            </button>
          </div>
        </div>
      }
    </div>
  </section>
</div>
